// rendezvousIndexedState — the pure indexed AND-join transition for the Indexed
// Rendezvous node.
//
// A barrier over an INDEX (the plain Rendezvous is the same idea with no index).
// Both sides carry an index. Side B arrivals mark an index ready, recorded
// order-independent — B(5) may arrive before B(3). Side A, when it arrives,
// fires the output once B has marked the SAME index ready — an EXACT match — in
// whichever order the two arrive:
//   B(k) already seen when A(k) lands  → fires immediately on A
//   A(k) lands first (k not yet ready) → A is held pending; fires when B(k) lands
// It does no arithmetic on the indices: if you need to join a completion of n
// with the readiness of n+1, add the +1 upstream in the graph (an increment node
// on the A-index wire) — the node's job is only the match.
//
// Idempotent like the plain Rendezvous: a repeated B(index) is absorbed (it's a
// set), and an index fires at most once per cycle (`fired`), so a duplicate A(k)
// after k already fired is a no-op. A second, DIFFERENT pending A supersedes the
// first — one request outstanding at a time. `reset` clears everything so a new
// cycle's indices start fresh (e.g. a new response renumbers sentences from 0),
// and is the boundary the node is wired to instead of a response epoch (the
// transport carries no response id; see SentenceController._signalDataReady).
//
// State is plain arrays (not Sets) so it stays JSON-comparable in tests; the
// runtime holds one state object per node id. Kept pure + unit-tested
// (tests/unit/rendezvousIndexedState.test.mjs) so the EventRuntime block is a
// thin adapter around it. No imports.

/**
 * @param {{ready?:number[], fired?:number[], pending?:number|null}|null|undefined} state
 * @param {'A'|'B'|'reset'} input   which input just fired
 * @param {number} [index]          A's or B's index (ignored for reset; non-finite = no index)
 * @returns {{ready:number[], fired:number[], pending:number|null, fire:boolean, index:number|null}}
 */
export function applyIndexedRendezvous(state, input, index) {
  const ready = new Set(state?.ready ?? [])
  const fired = new Set(state?.fired ?? [])
  let pending = state?.pending ?? null

  const pack = (fire, firedIndex) => ({
    ready: [...ready],
    fired: [...fired],
    pending,
    fire,
    index: fire ? firedIndex : null,
  })

  if (input === 'reset') {
    ready.clear()
    fired.clear()
    pending = null
    return pack(false, null)
  }

  if (input === 'B') {
    if (Number.isFinite(index)) ready.add(index)
    // A pending A is satisfiable iff its index just became (or already is) ready
    // and hasn't already fired this cycle.
    if (pending !== null && ready.has(pending) && !fired.has(pending)) {
      const k = pending
      fired.add(k)
      pending = null
      return pack(true, k)
    }
    return pack(false, null)
  }

  if (input === 'A') {
    if (!Number.isFinite(index)) return pack(false, null)  // no index to match on
    if (fired.has(index)) return pack(false, null)         // idempotent: already fired this cycle
    if (ready.has(index)) {
      fired.add(index)
      pending = null
      return pack(true, index)
    }
    pending = index                                        // wait for B(index); supersedes any prior pending
    return pack(false, null)
  }

  return pack(false, null)  // unknown input — record nothing
}
